import { fontSizes } from "@dana/MobiusOverrides/styles/typography";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
});
type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsPageTitleStyles {
    container?: GridContainerProps;
    brandGridItem?: GridItemProps;
    brandStyles?: ProductBrandStyles;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    numberText?: TypographyProps;
    badgeRed?: TypographyPresentationProps;
    badgeGridItem?: GridItemProps;
    messageStyles?: InjectableCss;
}

export const pageTitleStyles: ProductDetailsPageTitleStyles = {
    container: {
        gap: 0,
        offsetCss: css`
            max-width: 100%;
        `,
    },
    brandGridItem: {
        width: 12,
    },
    brandStyles: {
        logoImage: {
            css: css`
                img {
                    max-width: 150px;
                    max-height: 150px;
                }
            `,
        },
        nameText: {
            size: 16,
            weight: "normal",
        },
    },
    titleGridItem: {
        width: 12,
    },
    titleText: {
        variant: "h3",
        css: css`
            margin-bottom: 16px;

            @media print {
                margin-bottom: 0;
                font-size: ${fontSizes.small};
            }
        `,
    },
    numberText: {
        variant: "h1",
        css: css`
            margin-top: 16px;
            margin-bottom: 10px;

            @media print {
                margin: 0;
                font-size: ${fontSizes.xSmall};
                line-height: 1;
            }
        `,
    },
    messageStyles:{
        css: css`
        padding: 0 0 10px 0;
        `
    }
};

const styles = pageTitleStyles;

const ProductDetailsPageTitle: React.FC<Props> = ({ product, session }) => {
    const isAuthenticated = session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    return (
        <GridContainer {...styles.container} className="product-details-page-title">
            {product.brand && (
                <GridItem {...styles.brandGridItem}>
                    <ProductBrand brand={product.brand} showLogo={true} extendedStyles={styles.brandStyles} />
                </GridItem>
            )}
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.numberText} data-test-selector="ProductDetailsPartNumber">
                    {product.detail?.modelNumber ?? product.productNumber}
                </Typography>
            </GridItem>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.titleText} data-test-selector="productDetails_productDescription">
                    {product.productTitle}
                </Typography>
            </GridItem>
            {product.properties.isReturnable !== "true" && isAuthenticated && (
                <StyledWrapper  {...styles.messageStyles}>
                    <GridItem {...styles.badgeGridItem}>
                        <Typography {...styles.badgeRed}>{translate("Non Returnable")}</Typography>
                    </GridItem>
                </StyledWrapper>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(ProductDetailsPageTitle)),
    definition: {
        displayName: "Page Title",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
